import React, { useCallback, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useAppDispatch } from '../../reducers'
import { useNavigate, useParams } from 'react-router-dom'
import { useTablePageParam } from '../../hooks/use-table-page-param.hook'
import {
  useTableSortParam,
  PROJECT_CONTENT_SORT_KEY
} from '../../hooks/use-table-sort-param.hook'
import capitalize from 'lodash/capitalize'
import orderBy from 'lodash/orderBy'
import { loadProjectContent } from '../../actions/project'
import { loadCoordinateSystems } from '../../actions/coordinateSystems'
import AppPropTypes from '../../shared/appPropTypes'
import messages from '../../shared/messages'
import DataTable from '../DataTable'
import Renders from '../DataTable/renders'
import DataTableRenders from '../DataTable/renders'
import DateTime from '../DataTable/renders/DateTime'
import LongText from '../DataTable/renders/LongText'
import LongName from '../DataTable/renders/LongName'
import { CONVERT_TYPE } from '../../helpers/convert'
import { DOWNLOAD_TYPE } from '../../helpers/export'
import { ReactComponent as DataSet } from '@mike/mike-shared-frontend/media/icons/DataSet'
import { ReactComponent as Folder } from '@mike/mike-shared-frontend/media/icons/Folder'
import { css } from '@emotion/css'
import { getCapabilities } from '../../apis/project'
import { useTypedSelector } from '../../reducers'
import { isDataset } from '../../helpers/projectContent'
import { MOVE_DATASET } from '../../helpers/move'
import { COPY_DATASET } from '../../helpers/copy'
import { getUserTenants as apiGetUserTenants } from '../../apis/datasetTransfer'
import useBreadcrumbTrail from '../../hooks/use-breadcrumb-trail.hook'
import { childTrailState } from '../../helpers/breadcrumbTrail'

type Tenant = { id: string; name: string; role?: string }
const toItems = (resp: any) =>
  Array.isArray(resp) ? resp : resp?.data ?? resp?.items ?? []

const ProjectContentTable = props => {
  const {
    projectContent,
    loading = false,
    selectedItems,
    onSelectionChange,
    onDelete,
    onEdit,
    onManageAccess,
    onConvert,
    onDownload,
    onHandleRequestSort,
    topOffset,
    parentCapabilities,
    processDroppedFiles,
    rowsPerPage,
    totalCount,
    sortBy,
    sortOrder,
    onMove,
    onCopy
  } = props

  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const routeParams = useParams<{ id: string }>()
  const routeProjectId = routeParams.id

  // Matches prior componentDidMount: loadCoordinateSystems once
  useEffect(() => {
    dispatch(loadCoordinateSystems())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadedProjectId = useTypedSelector(
    state => state['project'].projectContent.projectId
  )

  useEffect(() => {
    if (routeProjectId && routeProjectId !== loadedProjectId) {
      dispatch(loadProjectContent(routeProjectId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeProjectId])

  const _columns = useMemo(
    () => [
      {
        field: 'name',
        label: intl.formatMessage(messages.name),
        render: itemNameRender,
        className: hoverStyle
      },
      {
        field: 'description',
        label: intl.formatMessage(messages.description),
        render: longTextRender,
        className: hoverStyle
      },
      {
        field: 'datasetType',
        label: intl.formatMessage(messages.type),
        render: formatRender,
        className: hoverStyle
      },
      {
        field: 'datasetFormat',
        label: intl.formatMessage(messages.format),
        render: formatRender,
        className: hoverStyle
      },
      {
        field: 'storageSize',
        label: intl.formatMessage(messages.size),
        render: DataTableRenders.size(1024),
        className: hoverStyle
      },
      {
        field: 'createdAt',
        label: intl.formatMessage(messages.createdAt),
        render: dateTimeRender,
        className: hoverStyle
      },
      {
        field: 'updatedAt',
        label: intl.formatMessage(messages.editedAt),
        render: dateTimeRender,
        className: hoverStyle
      }
    ],
    [intl]
  )
  const currentTenantId =
    useTypedSelector(state => state.auth?.user?.tenantId) || ''
  const tenantsFromStore = useTypedSelector(s => s.tenant.tenants) as
    | Tenant[]
    | undefined

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loadingTenants, setLoadingTenants] = useState(false)

  useEffect(() => {
    if (tenantsFromStore?.length) {
      setTenants(tenantsFromStore)
      setLoadingTenants(false)
      return
    }

    let stale = false
    setLoadingTenants(true)
    apiGetUserTenants()
      .then(d => !stale && setTenants(toItems(d)))
      .catch(() => undefined)
      .finally(() => setLoadingTenants(false))

    return () => {
      stale = true
    }
  }, [tenantsFromStore])

  const isOwnerHere = useMemo(
    () =>
      tenants.some(
        t =>
          t.id === currentTenantId &&
          String(t.role || '').toLowerCase() === 'owner'
      ),
    [tenants, currentTenantId]
  )
  const isOwnerElsewhere = useMemo(
    () =>
      tenants.some(
        t =>
          t.id !== currentTenantId &&
          String(t.role || '').toLowerCase() === 'owner'
      ),
    [tenants, currentTenantId]
  )
  const allowCrossSiteTransfer = isOwnerHere && isOwnerElsewhere

  const [capabilities, setCapabilities] = useState({})
  const fetchCapabilitiesIfNeeded = useCallback(
    item => {
      if (
        !isDataset(item) &&
        (capabilities[item.id] === null || capabilities[item.id] === undefined)
      ) {
        return getCapabilities(item.id).then(newCap => {
          setCapabilities(prev => ({ ...prev, [item.id]: newCap }))
          return newCap
        })
      }
      return Promise.resolve(capabilities[item.id])
    },
    [capabilities]
  )

  const actionPopupStateChange = (open, row) => {
    if (open) fetchCapabilitiesIfNeeded(row)
  }

  const onSelectionChangeWrapper = items => {
    items.forEach(fetchCapabilitiesIfNeeded)
    onSelectionChange(items)
  }

  const breadcrumbs = useBreadcrumbTrail()

  const onColumnClick = React.useCallback(
    item => {
      fetchCapabilitiesIfNeeded(item).then(cap => {
        if (!cap?.canListContent) return
        const state = childTrailState(
          breadcrumbs,
          loadedProjectId,
          routeProjectId,
          { id: item.id, name: item.name }
        )
        navigate(`/project/${item.id}`, { state })
      })
    },
    [
      fetchCapabilitiesIfNeeded,
      navigate,
      breadcrumbs,
      loadedProjectId,
      routeProjectId
    ]
  )

  const [sort, persistSort] = useTableSortParam(PROJECT_CONTENT_SORT_KEY, {
    orderBy: sortBy,
    sortOrder: sortOrder
  })
  const localSortBy = sort.orderBy
  const localSortOrder = sort.sortOrder
  const [localPage, setLocalPage] = useTablePageParam()
  const [localRowsPerPage, setLocalRowsPerPage] = useState<number>(
    () =>
      parseInt(
        localStorage.getItem('project-content-rowsPerPage') ??
          String(rowsPerPage),
        10
      ) || rowsPerPage
  )

  const sortedContent = useMemo(() => {
    const getSorter = field => {
      switch (field.toLowerCase()) {
        case 'name':
          return item => item.name.toLowerCase()
        case 'storagesize':
        case 'size':
          return item => item.storageSize || 0
        case 'createdat':
          return item => new Date(item.createdAt).getTime()
        case 'updatedat':
          return item => new Date(item.updatedAt).getTime()
        default:
          return field
      }
    }

    const sorter = getSorter(localSortBy)
    return orderBy(projectContent, [sorter], [localSortOrder.toLowerCase()])
  }, [projectContent, localSortBy, localSortOrder])

  const paginatedContent = useMemo(() => {
    const startIndex = localPage * localRowsPerPage
    const endIndex = startIndex + localRowsPerPage
    return sortedContent.slice(startIndex, endIndex)
  }, [sortedContent, localPage, localRowsPerPage])

  const items = useMemo(() => {
    return paginatedContent.map(item => ({
      ...item,
      capabilities: capabilities[item.id] || null
    }))
  }, [paginatedContent, capabilities])

  const handleLocalSort = useCallback(
    (orderBy, order) => {
      persistSort(orderBy, order)
      setLocalPage(0)
    },
    [persistSort, setLocalPage]
  )

  const handleLocalPageChange = useCallback(
    newPage => {
      setLocalPage(newPage)
    },
    [setLocalPage]
  )

  const handleLocalRowsPerPageChange = useCallback(
    newRowsPerPage => {
      const parsed = parseInt(String(newRowsPerPage), 10) || localRowsPerPage
      localStorage.setItem('project-content-rowsPerPage', String(parsed))
      setLocalRowsPerPage(parsed)
      setLocalPage(0)
    },
    [localRowsPerPage, setLocalPage]
  )

  const mergedParentCaps = useMemo(
    () => ({ ...parentCapabilities, allowCrossSiteTransfer }),
    [parentCapabilities, allowCrossSiteTransfer]
  )

  const actions = useMemo(() => {
    const base = [
      {
        name: 'Edit',
        callBack: onEdit,
        render: Renders.renderEditItem,
        disableGutters: true,
        parentCapabilities: mergedParentCaps
      },
      {
        name: 'Folder Access',
        callBack: onManageAccess,
        render: Renders.renderManageAccess,
        disableGutters: true,
        parentCapabilities: mergedParentCaps
      },
      {
        name: 'Delete',
        callBack: onDelete,
        render: Renders.renderDeleteItem,
        disableGutters: true,
        parentCapabilities: mergedParentCaps
      },
      {
        name: CONVERT_TYPE,
        callBack: onConvert,
        render: Renders.renderConvertDataset,
        disableGutters: true,
        parentCapabilities: mergedParentCaps
      },
      {
        name: DOWNLOAD_TYPE,
        callBack: onDownload,
        render: Renders.renderDownloadDataset,
        disableGutters: true,
        parentCapabilities: mergedParentCaps
      }
    ]

    if (allowCrossSiteTransfer) {
      base.push(
        {
          name: MOVE_DATASET,
          callBack: onMove,
          render: Renders.renderMoveDataset,
          disableGutters: true,
          parentCapabilities: mergedParentCaps
        },
        {
          name: COPY_DATASET,
          callBack: onCopy,
          render: Renders.renderCopyDataset,
          disableGutters: true,
          parentCapabilities: mergedParentCaps
        }
      )
    }
    return base
  }, [
    onEdit,
    onDelete,
    onConvert,
    onDownload,
    onMove,
    onCopy,
    mergedParentCaps,
    allowCrossSiteTransfer,
    onManageAccess
  ])

  return (
    <DataTable
      actions={actions}
      actionsPopupStateChange={actionPopupStateChange}
      loading={loading || loadingTenants}
      columns={_columns}
      idField="id"
      data={items}
      selectedRows={selectedItems}
      onColumnClick={onColumnClick}
      onSelectionChange={onSelectionChangeWrapper}
      onHandleRequestSort={handleLocalSort}
      page={localPage}
      rowsPerPage={localRowsPerPage}
      topOffset={topOffset}
      onChangePage={handleLocalPageChange}
      onChangeRowsPerPage={handleLocalRowsPerPageChange}
      processDroppedFiles={processDroppedFiles}
      totalCount={totalCount}
      _order={[localSortOrder]}
      _orderBy={[localSortBy]}
    />
  )
}

ProjectContentTable.propTypes = {
  projectContent: PropTypes.arrayOf(AppPropTypes.dataset),
  selectedItems: PropTypes.arrayOf(AppPropTypes.dataset),
  loading: PropTypes.bool,
  searchText: PropTypes.string,
  onSelectionChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onManageAccess: PropTypes.func.isRequired,
  onConvert: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onHandleRequestSort: PropTypes.func.isRequired,
  topOffset: PropTypes.number,
  parentCapabilities: PropTypes.object,
  processDroppedFiles: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onMove: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired
}

const formatRender = value => (value ? capitalize(value) : 'Folder')
const longTextRender = value => <LongText longText={value} />
const itemNameRender = (value, item) => (
  <LongName longName={value} icon={'datasetType' in item ? DataSet : Folder} />
)
const dateTimeRender = value => <DateTime jsonTime={value} />
const hoverStyle = item =>
  'datasetType' in item
    ? null
    : css`
        &:hover {
          cursor: pointer;
        }
      `

export default ProjectContentTable
